/**
 * LegalContent — elegant typographic renderer for numbered legal sections.
 *
 * Accepts two kinds of blocks:
 *   - <LegalParagraph>  (plain paragraph)
 *   - <LegalSection number="1" title="...">  (numbered heading + body)
 *   - <LegalList>        (gold-bulleted list, used inside sections)
 *
 * Keeps the same moody palette as the rest of the product: serif heading,
 * thin gold index, textMid body, 1.75 leading for long-form readability.
 */

export function LegalParagraph({ children }) {
    return (
        <p className="text-ink-textMid leading-[1.75]">
            {children}
        </p>
    );
}

export function LegalSection({ number, title, children }) {
    return (
        <section className="mb-10">
            <h2 className="font-serif text-2xl md:text-[1.7rem] text-ink-textHigh mb-4 flex items-baseline gap-3">
                {number && (
                    <span className="text-ink-gold text-sm tracking-widest">{String(number).padStart(2, "0")}</span>
                )}
                <span>{title}</span>
            </h2>
            <div className="text-ink-textMid leading-[1.75] space-y-3">
                {children}
            </div>
        </section>
    );
}

export function LegalList({ items }) {
    return (
        <ul className="space-y-2">
            {items.map((it, i) => (
                <li key={i} className="flex items-start gap-3">
                    <span className="text-ink-gold/70 mt-[10px] block w-1.5 h-1.5 rounded-full flex-shrink-0" aria-hidden="true" />
                    <span>{it}</span>
                </li>
            ))}
        </ul>
    );
}

export function LegalPreamble({ children }) {
    return (
        <div className="mb-10 pl-4 border-l-2 border-ink-gold/40 italic font-serif text-ink-textHigh text-lg leading-relaxed">
            {children}
        </div>
    );
}

export function LegalContact({ label, value, mailto }) {
    return (
        <p className="text-ink-textMid leading-relaxed">
            <span className="label-eyebrow text-ink-gold mr-2">{label}</span>
            {mailto ? (
                <a
                    href={`mailto:${value}`}
                    className="text-ink-textHigh underline underline-offset-4 decoration-ink-gold/40 hover:decoration-ink-gold transition-colors"
                    data-testid="legal-contact-mailto"
                >
                    {value}
                </a>
            ) : (
                <span className="text-ink-textHigh">{value}</span>
            )}
        </p>
    );
}
